import { log } from '~/utils/log';

import ImpressionEvent, { type ImpressionType } from './ImpressionEvent';

export type ModuleDisplayType = 'block' | 'carousel' | 'full-bleed' | 'grid';

export const SITE_MODULE_TYPES = [
  'article-header',
  'article-latest-news',
  'recent-features'
] as const;

export type SiteModuleType = (typeof SITE_MODULE_TYPES)[number];

export type ModuleIdentity =
  | { entityId: string; moduleType: string }
  | { entityId?: never; moduleType: SiteModuleType };

export type ModuleImpressionProperties = {
  column_count?: number;
  display_type?: ModuleDisplayType;
  item_count?: number;
  module_position?: number;
  module_type: string;
};

type EventData = ModuleIdentity & {
  columnCount?: number;
  displayType?: ModuleDisplayType;
  guestId?: string;
  isBot: boolean;
  itemCount?: number;
  modulePosition?: number;
};

function isSiteModule(moduleType: string): moduleType is SiteModuleType {
  return (SITE_MODULE_TYPES as readonly string[]).includes(moduleType);
}

class ModuleImpressionEvent extends ImpressionEvent<
  EventData,
  ModuleImpressionProperties
> {
  protected get dedupeId(): string | undefined {
    const { entityId, modulePosition, moduleType } = this.data;

    if (entityId) return `${entityId}|${modulePosition}`;

    if (!isSiteModule(moduleType)) {
      log.error('Module impression fired without an entity_id', {
        module_type: moduleType
      });
    }

    return undefined;
  }

  protected get impressionType(): ImpressionType {
    return 'module';
  }

  protected getImpressionProperties(): ModuleImpressionProperties {
    return {
      column_count: this.data.columnCount,
      display_type: this.data.displayType,
      item_count: this.data.itemCount,
      module_position: this.data.modulePosition,
      module_type: this.data.moduleType
    };
  }
}

export default ModuleImpressionEvent;
